import React, { useState, useCallback } from 'react';
import dynamic from 'next/dynamic';
import Link from 'components/link';
import { buildClassName } from 'utils/build-class-name';
import { useFeaturesContext, useUserContext, useModalContext, LOGIN_REGISTRATION_MODAL } from 'contexts';
import { useIsMobile, useIsIpadMini } from 'hooks/use-size-class';
import { SquareUserIcon, SquareMenuIcon, SquareSearchIcon, SquareCloseIcon } from 'components/icon/square';
import SearchIcon from 'components/icon/search-icon';
import { headerIds, testIds } from 'constants/test-constants';
import { zoocasaNavigationConfig } from 'tenants/zoocasa/config/navigation';
import { useLocationResolution } from 'hooks/use-location-resolution';
import { SEARCH_PAGE_ROUTE_NAME } from 'types/search_page_types';
import { AREA_LISTINGS_ROUTE, RouteName } from 'components/dynamic-page/route-matchers';
import ZoocasaTheme from '@tenants/definitions/zoocasa/theme-config';
import styles from '../style.module.scss';

// Dynamically imported components to reduce initial bundle size
const AdvancedSearch = dynamic(() => import('components/search-bar/advanced-search'), {
  loading: () => <></>,
  ssr: false,
});

const ChatWidget = dynamic(() => import('components/chat-widget'), {
  loading: () => <></>,
  ssr: false,
});

const Panel = dynamic(() => import('components/panel/NavigationPanel'), {
  loading: () => <></>,
  ssr: true,
});

// Type definitions
interface PanelItem {
  id: string;
  label: string;
  href?: string;
  action?: string;
}

interface MobileSectionProps {
  userLinks: PanelItem[];
  routeName?: RouteName;
  mobileUserTrigger?: React.ReactNode;
  mobileMenuTrigger?: React.ReactNode;
  panelClassName?: string;
}

interface PanelState {
  isActive: boolean;
  items: PanelItem[];
  title: string;
}

/**
 * MobileSection Component
 * Renders the mobile version of the navigation header
 * Includes search functionality, user menu, and main navigation panel
 */
const MobileSection = ({ userLinks, routeName, mobileUserTrigger, mobileMenuTrigger, panelClassName }: MobileSectionProps) => {
  const { features, isWebView, isSearchPanelOpen, setIsSearchPanelOpen } = useFeaturesContext();
  const { isAuthenticated, isCrawler } = useUserContext();
  const { openModal } = useModalContext();
  const isMobile = useIsMobile();
  const isIpadMini = useIsIpadMini();
  
  // Device and route-specific flags
  const shouldShowMobileView = isMobile && !isIpadMini;
  const shouldShowDesktopView = !isMobile || isIpadMini;
  const isChatWidgetActive = features.useSmartChat && !isWebView && shouldShowMobileView;
  const resolvedLocation = useLocationResolution();

  const isSearchPage = routeName === SEARCH_PAGE_ROUTE_NAME;
  const isAreaPage = routeName === AREA_LISTINGS_ROUTE;
  const isSearchOrAreaPage = isSearchPage || isAreaPage;

  // Initialize panel state with crawler-specific values
  const [panelAttributes, setPanelAttributes] = useState<PanelState>({
    isActive: isCrawler, // Pre-render for crawlers
    items: [],
    title: isCrawler ? 'Menu' : '',
  });

  // Toggle search panel visibility
  const handleToggleSearch = () => {
    setIsSearchPanelOpen(!isSearchPanelOpen);
  };

  // Handle panel activation for user menu or main menu
  const handleTriggerPanel = (type: string) => {
    if (type === 'user' && !isAuthenticated) {
      openModal(LOGIN_REGISTRATION_MODAL);
    } else {
      if (type === 'user') {
        setPanelAttributes({ isActive: true, items: userLinks, title: 'My Account' });
      } else {
        setPanelAttributes({ isActive: true, items: ZoocasaTheme.menuLinks, title: 'Menu' });
      }
    }
  };

  // Close panel handler
  const handleClosePanel = useCallback(() => 
    setPanelAttributes({ isActive: false, items: [], title: '' }), []
  );

  return (
    <>
      <div className={styles['mobile-right']}>
        {/* Chat Widget */}
        {isChatWidgetActive && <ChatWidget />}
        
        {/* Mobile Search Panel */}
        {isSearchPanelOpen && shouldShowMobileView && (
          <div className={styles.panel}>
            <AdvancedSearch 
              isInPanel={true} 
              isFilterButtonHidden={true} 
              hideSearchPanel={() => setIsSearchPanelOpen(false)} 
              testId={testIds.advancedSearchMobile} 
            />
            <div className={styles['full-screen-search']} />
          </div>
        )}
        
        {/* Search Icon - Only shown on search/area pages, toggles to X when panel is open */}
        {isSearchOrAreaPage && shouldShowMobileView && (
          <div className={styles.search}>
            {isSearchPanelOpen ? (
              <SquareCloseIcon
                className={styles['header-icon']}
                onClick={handleToggleSearch}
                testId={headerIds.headerSearchIconMobile}
              />
            ) : (
              <SquareSearchIcon 
                className={styles['header-icon']} 
                onClick={handleToggleSearch} 
                testId={headerIds.headerSearchIconMobile} 
              />
            )}
          </div>
        )}
        
        {/* Map Link - Shown when not on search/area pages */}
        {(!isSearchOrAreaPage || shouldShowDesktopView) && (
          <Link 
            href="/search" 
            testId={headerIds.mapHeaderMobile} 
            className={styles['zoocasa-search-button-mobile']}
          >
            <SearchIcon className={styles['search-icon-mobile']} />
            Map
          </Link>
        )}
        
        {/* User Menu Button */}
        <div 
          id="panel-opener" 
          onClick={() => handleTriggerPanel('user')} 
          data-testid={headerIds.loginMobile}
          className={mobileUserTrigger ? styles['custom-user-trigger'] : ''}
        >
          {mobileUserTrigger || <SquareUserIcon className={buildClassName(styles['header-icon'], styles.unclickable)} />}
        </div>
        
        {/* Main Menu Button */}
        <div 
          id="panel-opener" 
          onClick={() => handleTriggerPanel('menu')} 
          data-testid={headerIds.mobileMenu}
          className={mobileMenuTrigger ? styles['custom-menu-trigger'] : ''}
        >
          {mobileMenuTrigger || <SquareMenuIcon isHoverable={true} className={styles.unclickable} />}
        </div>
      </div>

      {/* Navigation Panel */}
      {Panel && (
        <Panel 
          isActive={panelAttributes.isActive}
          items={panelAttributes.items}
          title={panelAttributes.title}
          className={panelClassName}
          closePanel={handleClosePanel}
          navigationConfig={zoocasaNavigationConfig}
          locationData={resolvedLocation}
          variant="mobile"
          isCrawler={isCrawler}
          userLinks={userLinks}
        />
      )}
    </>
  );
};

export default MobileSection; 